import {
  DEFAULT_LEAD_TIME_PROFILE,
  LEAD_TIME_PROVIDERS,
  type LeadTimeComponent,
  type LeadTimeComponentOverride,
  type LeadTimeComponentSource,
  type LeadTimeProfile,
} from '@erp/shared-types';

export function calculatedSourceForComponent(
  component: LeadTimeComponent,
): LeadTimeComponentSource | null {
  const interval = `${component.startsAt}:${component.endsAt}`;
  const provider = LEAD_TIME_PROVIDERS.find((row) =>
    row.intervals.some((value) => value === interval),
  );
  return provider ? { kind: 'calculated', providerId: provider.id } : null;
}

export function componentSourceChoice(
  component: LeadTimeComponent,
  choice: string,
): LeadTimeComponentSource {
  if (choice === 'static') return { kind: 'static', days: 0 };
  return calculatedSourceForComponent(component) ?? component.source;
}

export function overrideFormValid(
  source: LeadTimeComponentSource,
  reason: string,
  reviewDate: string,
  expiryDate: string,
): boolean {
  if (!reason.trim() || (!reviewDate && !expiryDate)) return false;
  if (source.kind !== 'static') return true;
  return (
    Number.isInteger(source.days) && source.days >= 0 && source.days <= 365
  );
}

function midnight(date: string): string | null {
  return date ? `${date}T00:00:00.000Z` : null;
}

export function overrideWindow(
  current: LeadTimeComponentOverride | undefined,
  startsDate: string,
  expiryDate: string,
  reviewDate: string,
) {
  return {
    startsAt: current ? undefined : (midnight(startsDate) ?? undefined),
    endsAt: midnight(expiryDate),
    reviewAt: midnight(reviewDate),
    supersedesOverrideId: current?.id ?? null,
  };
}

export function overrideLifecycle(
  row: LeadTimeComponentOverride,
  now: number,
): 'revoked' | 'expired' | 'scheduled' | 'review_overdue' | 'active' {
  if (row.deactivatedAt) return 'revoked';
  if (row.endsAt && Date.parse(row.endsAt) <= now) return 'expired';
  if (Date.parse(row.startsAt) > now) return 'scheduled';
  if (row.reviewAt && Date.parse(row.reviewAt) <= now) return 'review_overdue';
  return 'active';
}

export function overrideCanRevoke(
  row: LeadTimeComponentOverride,
  now: number,
): boolean {
  const status = overrideLifecycle(row, now);
  return status !== 'revoked' && status !== 'expired';
}

export function activeComponentOverride(
  rows: LeadTimeComponentOverride[],
  componentId: string,
  now: number,
) {
  return rows.find(
    (row) =>
      row.componentId === componentId &&
      ['active', 'review_overdue'].includes(overrideLifecycle(row, now)),
  );
}

export function defaultProfileDraft(
  labels: Record<string, string>,
): LeadTimeProfile {
  return {
    ...DEFAULT_LEAD_TIME_PROFILE,
    components: DEFAULT_LEAD_TIME_PROFILE.components.map((component) => ({
      ...component,
      label: labels[component.id] ?? component.label,
    })),
  };
}

export function appendProfileComponent(
  draft: LeadTimeProfile,
  label: string,
): LeadTimeProfile {
  const previous = draft.components.at(-1);
  const component: LeadTimeComponent = {
    id: crypto.randomUUID(),
    label,
    sequence: (previous?.sequence ?? -1) + 1,
    startsAt: previous?.endsAt ?? 'need_recognized',
    endsAt: `boundary_${draft.components.length + 1}`,
    source: { kind: 'static', days: 0 },
  };
  return { ...draft, components: [...draft.components, component] };
}

export function overrideDraftDefaults(
  component: LeadTimeComponent,
  current?: LeadTimeComponentOverride,
) {
  if (!current)
    return { source: component.source, expiryDate: '', reviewDate: '' };
  return {
    source: current.source,
    expiryDate: current.endsAt?.slice(0, 10) ?? '',
    reviewDate: current.reviewAt?.slice(0, 10) ?? '',
  };
}
